import {calculateChordDistance, calculateKeyOfSong, extractChordInfo, transposeChord} from "./chord-operations";
import {ChordInfo} from "./chord-operations"

interface Chord {
    chord: string;
    index: number;
    prefix: string;
    suffix: string;
}

interface Line {
    type: 'chord' | 'text';
    content: Chord[] | string;
    lineNumber?: number;
}

interface Song {
    lines: Line[];
    tags: string[];
    keys: string[];
    artists: string[];
    title: string;
    displayKeys: string[];
    lineStart?: number;
    lineEnd?: number;
    notation?: string;
    sections?: Section[];
    currentPosition?: number;
    originalPosition?: number;
    originalKey?: string;
}

interface Section {
    typeOfVerse: string;
    key: string | null;
    number: number;
    content: Line[][];
}

interface KeyResult {
    keys: string[];
    notation: string;
    isKnown: boolean;
}

export class JsonToSong {
    constructor() {
    }

    songsEnriched(allSongs: { lines: Line[] }): Song[] {
        let currentTitle = '000000';
        let songsEnriched: { [key: string]: Song } = {
            [currentTitle]: {lines: [], tags: [], keys: [], artists: [], title: '000000', displayKeys: [], lineStart: 1, lineEnd: 0}
        };
        let titleId = 0;
        let currentLineNumber = 1;

        for (const line of allSongs.lines) {
            currentLineNumber = line.lineNumber ?? currentLineNumber;
            if (line.type === 'text') {
                const match = (line.content as string).match(/^\s*\[((Title|Tag|Artist|Key|Notation|DisplayKey):\s*(.*))\]\s*$/i);
                if (match) {
                    const tagKey = match[2]?.toLowerCase();
                    const tagValue = match[3].trim();

                    if (tagKey === 'title') {
                        songsEnriched[currentTitle].lineEnd = currentLineNumber - 1;
                        titleId += 1;
                        currentTitle = `${tagValue}[${titleId}]`;
                        if (!songsEnriched[currentTitle]) {
                            songsEnriched[currentTitle] = {
                                lines: [],
                                tags: [],
                                keys: [],
                                artists: [],
                                displayKeys: [],
                                title: tagValue,
                                lineStart: currentLineNumber,
                                lineEnd: 0
                            };
                        }
                    } else if (tagKey === 'tag') {
                        songsEnriched[currentTitle].tags.push(tagValue);
                    } else if (tagKey === 'artist') {
                        songsEnriched[currentTitle].artists.push(tagValue);
                    } else if (tagKey === 'key') {
                        songsEnriched[currentTitle].keys.push(tagValue);
                    } else if (tagKey === 'displaykey') {
                        songsEnriched[currentTitle].displayKeys.push(tagValue);
                    } else if (tagKey === 'notation') {
                        songsEnriched[currentTitle].notation = tagValue;
                    }
                }
            }
            songsEnriched[currentTitle].lines.push(line);
            currentLineNumber++;
        }

        songsEnriched[currentTitle].lineEnd = currentLineNumber - 1;

        return Object.values(songsEnriched)
            .filter(song => song.title !== '000000')
            .map(song => this.removeExtraEmptyLines(song))
            .map(song => this.addMissingKeyAndNotation(song))
            .map(song => this.addSections(song))
            .map((song, i) => ({...song, currentPosition: i + 1, originalPosition: i + 1}));
    }

    removeExtraEmptyLines(song: Song): Song {
        const newSong: Song = JSON.parse(JSON.stringify(song));
        newSong.lines = [];
        let prevLineEmpty = true;

        for (const line of song.lines) {
            if (line.type === 'text') {
                line.content = (line.content as string).replace(/\s+$/, '');
                const isEmpty = (line.content as string).trim() === '';

                if (isEmpty) {
                    if (!prevLineEmpty) {
                        newSong.lines.push(line);
                        prevLineEmpty = true;
                    }
                } else {
                    newSong.lines.push(line);
                    prevLineEmpty = false;
                }
            } else {
                newSong.lines.push(line);
            }
        }

        while (newSong.lines.length > 0 &&
        newSong.lines[0].type === 'text' &&
        (newSong.lines[0].content as string).trim() === '') {
            newSong.lines.shift();
        }

        while (newSong.lines.length > 0 &&
        newSong.lines[newSong.lines.length - 1].type === 'text' &&
        (newSong.lines[newSong.lines.length - 1].content as string).trim() === '') {
            newSong.lines.pop();
        }

        return newSong;
    }

    addMissingKeyAndNotation(song: Song): Song {
        // Determine initial assumption for Polish notation based on existing tag or chord patterns
        let isPolishAssumption = song.notation === 'polish';
        if (song.notation === undefined || song.notation === null) {
            const chordsContent = song.lines.filter(line => line.type === 'chord')
                .flatMap(line => line.content as Chord[]);
            const probablyPL = chordsContent.some(content => content.chord.match(/^[Hh](.*)?/));
            const probablyInt = chordsContent.some(content => content.chord.match(/^[A-Ga-g]#(.*)?/));
            // This condition mirrors the JS logic: (probablyPL && !probablyInt) || song.notation === 'polish'
            // When song.notation is null/undefined, it relies on probablyPL && !probablyInt
            isPolishAssumption = (probablyPL && !probablyInt);
        }

        // Extract chord info using the initial assumption
        let infos = song.lines.filter(line => line.type === 'chord')
            .flatMap(line => line.content as Chord[])
            .map(content => extractChordInfo(content.chord, isPolishAssumption));

        // Determine final notation based on the system identified by extractChordInfo
        let knownSystemInfos = infos.filter(info => info.system === 'polish');
        let isPolish = knownSystemInfos.length > 0;

        // Check for B base note (international B) among extracted chords
        let hasB = infos.some(it => it.baseNote === 'B');

        // Calculate initial key based on extracted infos
        let keys = this.getKeyOfSong(infos, song);

        // Refine notation if key is unknown, not initially Polish, and contains 'B' chords
        if (!keys.isKnown && !isPolish && hasB) {
            // Re-extract assuming Polish
            let infosAssumePolish = song.lines.filter(line => line.type === 'chord')
                .flatMap(line => line.content as Chord[])
                .map(content => extractChordInfo(content.chord, true));

            // Compare key fitness
            let keysPolish = this.getKeyOfSong(infosAssumePolish, song);
            let notPl = calculateKeyOfSong(infos);
            let inPl = calculateKeyOfSong(infosAssumePolish);

            // If Polish assumption fits better, update isPolish and keys
            if (inPl.length > 0 && notPl.length > 0 && inPl[0].notInKey < notPl[0].notInKey) {
                isPolish = true;
                keys = keysPolish;
            }
        }

        // Final assignment of keys and notation
        song.keys = keys.keys;
        song.notation = isPolish ? 'polish' : 'international';

        // Apply a zero-semitone transpose to ensure all chords conform to the final notation
        song.lines = song.lines.map(line => {
            if (line.type === 'chord') {
                // Pass the FINAL determined isPolish flag
                line.content = this.transposeLine(line.content as Chord[], isPolish, 0, isPolish);
            }
            return line;
        });

        return song;
    }

    transposeLine(content: Chord[], isPolish: boolean, semitones: number, targetPolish?: boolean): Chord[] {
        return content.map((chord, index, chordsArray) => {
            const info = extractChordInfo(chord.chord, isPolish);
            if (targetPolish !== undefined) info.isSongInPolish = !!targetPolish;

            const transposedChord = transposeChord(info, semitones);
            const difference = transposedChord.length - chord.chord.length;
            chord.chord = transposedChord;

            if (index < chordsArray.length - 1) {
                const nextChord = chordsArray[index + 1];

                if (difference > 0) {
                    const availableSpace = nextChord.prefix.length - 1;
                    const spaceToBorrow = Math.min(difference, availableSpace);
                    nextChord.prefix = nextChord.prefix.substring(spaceToBorrow);
                } else if (difference < 0) {
                    nextChord.prefix = ' '.repeat(-difference) + nextChord.prefix;
                }
            }

            return chord;
        });
    }

    getKeyOfSong(chordInfos: ChordInfo[], song: Song): KeyResult {
        if (song.keys?.length > 0 && song.notation) {
            return {keys: song.keys, notation: song.notation, isKnown: true};
        }

        const calculated = calculateKeyOfSong(chordInfos);
        return {
            keys: calculated.map(key => key.key),
            notation: song.notation || 'international',
            isKnown: song.notation !== undefined && song.notation !== null
        };
    }

    addSections(song: Song): Song {
        let currentSectionType = '-';
        let currentSectionContent: Line[] = [];
        let previousSectionContent: Line[] = [];
        let sections: Section[] = [];
        let sectionNumber = 1;
        let lastRefrain: Section | null = null;

        const groupLines = (sectionContent: Line[]): Line[][] => {
            const groupedLines: Line[][] = [];
            let currentLinesGroup: Line[] = [];

            sectionContent.forEach(line => {
                if (line.type === 'chord' || (line.type === 'text' && (line.content as string).match(/^\s*(\[.*\])?\s*$/i))) {
                    if (currentLinesGroup.length > 0) groupedLines.push(currentLinesGroup);
                    currentLinesGroup = [];
                }
                currentLinesGroup.push(line);
            });

            if (currentLinesGroup.length > 0) groupedLines.push(currentLinesGroup);
            return groupedLines;
        };

        const pushSection = () => {
            const v_previous = this.getTypeOfVerse(previousSectionContent);
            const v_current = this.getTypeOfVerse(currentSectionContent);
            const vt_previous = v_previous.type;
            const vt_current = v_current.type;

            if ((vt_previous === 'refrain-ref' && lastRefrain == null || vt_previous === 'refrain') &&
                vt_current === 'verse') {
                const pop = sections.pop()!;
                sections.push({
                    typeOfVerse: 'refrain',
                    key: pop.key || v_previous.tagKey,
                    number: pop.number,
                    content: [[...pop.content.flat()], ...groupLines(currentSectionContent)]
                });
                lastRefrain = sections[sections.length - 1];
            } else if (vt_previous === 'refrain' && vt_current === 'verse') {
                const pop = sections.pop()!;
                sections.push({
                    typeOfVerse: 'refrain',
                    key: pop.key || v_previous.tagKey,
                    number: pop.number,
                    content: [[...pop.content.flat()], ...groupLines(currentSectionContent)]
                });
            } else if (vt_previous === 'meta' && vt_current === 'verse') {
                const pop = sections.pop()!;
                sections.push({
                    typeOfVerse: 'verse',
                    key: pop.key || v_previous.tagKey,
                    number: pop.number,
                    content: [[...pop.content.flat()], ...groupLines(currentSectionContent)]
                });
            } else {
                sections.push({
                    typeOfVerse: vt_current,
                    key: v_current.tagKey,
                    number: sectionNumber++,
                    content: groupLines(currentSectionContent)
                });
            }
        };

        for (let i = 0; i < song.lines.length; i++) {
            const line = song.lines[i];
            line.lineNumber = line.lineNumber ?? (i + 1);
            const prevSectionType = currentSectionType;

            if (line.type !== 'chord' &&
                ((line.content as string).trim().length === 0 || (line.content as string).match(/^\s*\[(.*)\]\s*$/i))) {
                currentSectionType = 'meta';
            } else {
                currentSectionType = 'verse';
            }

            const isSectionLabel = (candidate: Line) => candidate.type === 'text'
                && /^\s*\[(?!Title:|Key:|Notation:|DisplayKey:|Tag:.+|Artist:|Url:|BPM:)(?:[\p{L}\p{N}_.-]+|(?:Verse|Chorus|Zwrotka|Ref)\s*\d+(?:\.\d+)*):?\]\s*$/iu.test(candidate.content as string);
            const splitLabels = prevSectionType === 'meta' && currentSectionType === 'meta'
                && isSectionLabel(line) && currentSectionContent.some(isSectionLabel);
            if ((prevSectionType !== currentSectionType && prevSectionType !== '-') || splitLabels) {
                pushSection();
                previousSectionContent = currentSectionContent;
                currentSectionContent = [line];
            } else {
                currentSectionContent.push(line);
            }
        }

        pushSection();
        song.sections = sections;
        return song;
    }

    getTypeOfVerse(currentSectionHeader: Line[]): { type: string; tagKey: string | null } {
        const refrainRefs = currentSectionHeader
            .filter(line => line.type !== 'chord' &&
                (line.content as string).startsWith('[') && (line.content as string).endsWith(']') &&
                /\[ref/i.test(line.content as string));

        const metas = currentSectionHeader
            .filter(line => line.type === 'text' &&
                ((line.content as string).startsWith('[') && (line.content as string).endsWith(']') ||
                    (line.content as string).trim().length === 0));

        const metas2 = currentSectionHeader
            .filter(line => line.type === 'text' &&
                (line.content as string).startsWith('[') && (line.content as string).endsWith(']'));

        const lastMetaTagKey = metas2.length > 0 ?
            ((metas2[metas2.length - 1].content as string).match(/^\s*\[\s*(([A-Z0-9_-]+):\s*)\]\s*$/i) || [])[2] : null;

        const refrains = refrainRefs.filter(line => (line.content as string).includes(':'));

        if (refrains.length >= 1) return {type: 'refrain', tagKey: lastMetaTagKey};
        if (refrainRefs.length >= 1) return {type: 'refrain-ref', tagKey: lastMetaTagKey};
        return {type: metas.length > 0 ? 'meta' : 'verse', tagKey: lastMetaTagKey};
    }

    transposeToKey(song: Song, majorKey: string): void {
        const distance = calculateChordDistance(song.keys[0], majorKey, song.notation === 'polish');
        this.transpose(song, distance);
    }

    transpose(song: Song, semitones: number): void {
        song.originalKey = song.originalKey || song.keys[0];
        song.keys = song.keys.map(key =>
            transposeChord(extractChordInfo(key, song.notation === 'polish'), semitones));

        song.lines = song.lines.map(line => {
            if (line.type === 'chord') {
                line.content = this.transposeLine(line.content as Chord[], song.notation === 'polish', semitones);
            }
            return line;
        });

        this.addSections(song);
    }

    songsTransposed(songsData: Song[]): Song[] {
        let result = JSON.parse(JSON.stringify(songsData));

        result = result.map((song: Song) => {
            if (song.displayKeys && song.displayKeys.length > 0 && song.keys && song.keys.length > 0) {
                const displayKey = song.displayKeys[0];
                const currentKey = song.keys[0];

                if (displayKey !== currentKey) {
                    this.transposeToKey(song, displayKey);
                }
            }
            return song;
        });

        return result;
    }

    songsWithNotation(songsData: Song[], preferredNotation: string): Song[] {
        let result = JSON.parse(JSON.stringify(songsData));

        if (preferredNotation === 'polish' || preferredNotation === 'international') {
            result = result.map((song: Song) => {
                if (song.notation && song.notation !== preferredNotation) {
                    this.changeNotation(song, preferredNotation);
                }
                return song;
            });
        }

        return result;
    }

    changeNotation(song: Song, notation: string): void {
        const localize = (key: string) => {
            const info = extractChordInfo(key, song.notation === 'polish');
            info.isSongInPolish = notation === 'polish';
            return transposeChord(info, 0);
        };
        song.keys = song.keys.map(localize);
        song.displayKeys = song.displayKeys.map(localize);
        if (song.originalKey) song.originalKey = localize(song.originalKey);
        song.lines = song.lines.map(line => {
            if (line.type === 'chord') {
                line.content = this.transposeLine(line.content as Chord[], song.notation === 'polish', 0, notation === 'polish');
            }
            return line;
        });

        song.notation = notation;
        this.addSections(song);
    }

    songToText(song: Song): string {
        const lines: string[] = [];

        if (song.title) {
            lines.push(`[Title: ${song.title}]`);
        }

        if (song.artists && song.artists.length > 0) {
            song.artists.forEach(artist => {
                lines.push(`[Artist: ${artist}]`);
            });
        }

        if (song.keys && song.keys.length > 0) {
            lines.push(`[Key: ${song.keys[0]}]`);
        }

        if (song.notation) {
            lines.push(`[Notation: ${song.notation}]`);
        }

        if (song.displayKeys && song.displayKeys.length > 0) {
            lines.push(`[DisplayKey: ${song.displayKeys[0]}]`);
        }

        if (song.tags && song.tags.length > 0) {
            song.tags.forEach(tag => {
                lines.push(`[Tag: ${tag}]`);
            });
        }

        lines.push('');

        for (const line of song.lines) {
            const isMetadata = line.type === 'text' &&
                (line.content as string).match(/^\s*\[((Title|Tag|Artist|Key|Notation|DisplayKey):\s*(.*))\]\s*$/i);
            if (isMetadata) continue;

            if (line.type === 'chord') {
                let chordLine = '';
                (line.content as Chord[]).forEach(chord => {
                    chordLine += chord.prefix + chord.chord + chord.suffix;
                });
                lines.push(chordLine);
            } else if (line.type === 'text') {
                lines.push(line.content as string);
            }
        }

        return lines.join('\n');
    }

    songsToText(songs: Song[]): string {
        return songs.map(song => this.songToText(song)).join('\n\n');
    }
}
